/**
 * Outgoing internal links of a venue showcase (`/locale/[slug]`).
 *
 * The showcase renders with `hasFooter={false} hasNavbar={false}`
 * (`components/showcase-v2/index.tsx`), and `venue-footer` navigated with
 * `window.open` handlers alone, so the served markup carried no `<a href>` at
 * all: 1.999 pages, 82% of the site, that pass authority to nothing. Ahrefs
 * Site Audit, 04/08/2026.
 *
 * The targets deliberately are NOT worker-app's own `/cerco-lavoro/[...slug]`
 * routes. Those render fine on `worker.restworld.it`, but on the canonical host
 * the marketing site redirects every `/cerco-lavoro/<path>` outside a fixed
 * allowlist to `/cerco-lavoro` with a 308 (website `vercel.json`), so
 * `/cerco-lavoro/milano` and `/cerco-lavoro/cameriere` are hub redirects for
 * Google, not landing pages.
 *
 * The slugs below are the published ones, i.e. the twin of `SEO_PAGES` in
 * website `src/lib/cerco-lavoro/config.ts`. Adding a page there is what makes it
 * linkable here; nothing in this file is composed from venue free text.
 */

/**
 * Those landing pages live in the marketing app, which has no preview twin
 * reachable from worker-app, so the canonical host is the only correct origin.
 * `BASE_URL` is worker-app's own base and would point at `dev.worker.…` outside
 * production.
 */
export const PUBLIC_SITE_ORIGIN = 'https://www.restworld.it';

/** How many links the footer block always shows. */
export const VENUE_INTERNAL_LINK_COUNT = 5;

/** At most this many of them are city-specific, so the fixed ones never vanish. */
const MAX_CITY_LINKS = 2;

/** Same 50 km the published city pages query with. */
const CITY_PAGE_RADIUS_METRES = 50000;

const EARTH_RADIUS_METRES = 6371000;

type CityDomain = 'cucina' | 'sala' | 'bar';

const CITY_DOMAIN_LABEL_KEYS: Record<CityDomain, string> = {
  cucina: 'kitchen_jobs_in_city',
  sala: 'dining_room_jobs_in_city',
  bar: 'bar_jobs_in_city',
};

type PublishedCity = {
  /** Second half of the page slug, e.g. the `milano` of `cucina-milano`. */
  slug: string;
  /**
   * The city name is translated and not taken from the slug: the English pages
   * read "Kitchen jobs in Milan", not "in Milano", and a link whose label
   * disagrees with its destination is a worse link.
   */
  nameKey: string;
  lat: number;
  lon: number;
  /**
   * In display order, and only the domains that have a published page: six of
   * the nine cities have `cucina` alone, so they contribute one link and the
   * fixed ones fill the rest.
   */
  domains: CityDomain[];
};

// Centres copied from the published pages, not from worker-app's `CITIES`:
// the two registries disagree on both coordinates and radius.
const PUBLISHED_CITIES: PublishedCity[] = [
  { slug: 'milano', nameKey: 'city_milano', lat: 45.4642, lon: 9.19, domains: ['cucina', 'sala', 'bar'] },
  { slug: 'roma', nameKey: 'city_roma', lat: 41.9028, lon: 12.4964, domains: ['cucina', 'sala', 'bar'] },
  { slug: 'torino', nameKey: 'city_torino', lat: 45.0703, lon: 7.6869, domains: ['cucina', 'sala', 'bar'] },
  { slug: 'verona', nameKey: 'city_verona', lat: 45.4384, lon: 10.9916, domains: ['cucina'] },
  { slug: 'bologna', nameKey: 'city_bologna', lat: 44.4949, lon: 11.3426, domains: ['cucina'] },
  { slug: 'treviso', nameKey: 'city_treviso', lat: 45.6669, lon: 12.2433, domains: ['cucina'] },
  { slug: 'firenze', nameKey: 'city_firenze', lat: 43.7696, lon: 11.2558, domains: ['cucina'] },
  { slug: 'padova', nameKey: 'city_padova', lat: 45.4064, lon: 11.8768, domains: ['cucina'] },
  { slug: 'brescia', nameKey: 'city_brescia', lat: 45.5416, lon: 10.2118, domains: ['cucina'] },
];

/**
 * Shown in this order, and used to top the block up to
 * `VENUE_INTERNAL_LINK_COUNT` when the venue has no published city near it,
 * which is the case for roughly 1.900 of the 2.981 public showcases.
 */
const CLUSTER_LINKS: VenueInternalLink[] = [
  { path: '/cerco-lavoro', labelKey: 'all_job_offers' },
  { path: '/cerco-lavoro/stagionale', labelKey: 'seasonal_jobs' },
  { path: '/cerco-lavoro/con-alloggio', labelKey: 'jobs_with_accommodation' },
  { path: '/cerco-lavoro/estero', labelKey: 'jobs_abroad' },
  { path: '/cerco-lavoro/stagionale-con-alloggio', labelKey: 'seasonal_jobs_with_accommodation' },
];

/** Every path this module is allowed to emit. Exported so the test can assert on it. */
export const PUBLISHED_LINK_PATHS: string[] = [
  ...CLUSTER_LINKS.map(({ path }) => path),
  ...PUBLISHED_CITIES.flatMap(({ slug, domains }) => domains.map((domain) => cityPagePath(domain, slug))),
];

export type VenueInternalLink = {
  /** Path on the public site, without the locale prefix. */
  path: string;
  /** Key in the `restaurant-showcase-v2` namespace. */
  labelKey: string;
  /** Key of the city name interpolated into `labelKey`, on a city link only. */
  cityNameKey?: string;
};

export type VenueInternalLinksSource = {
  lat?: number | null;
  lon?: number | null;
  approximateLat?: number | null;
  approximateLon?: number | null;
};

export function buildVenueInternalLinks(source?: VenueInternalLinksSource | null): VenueInternalLink[] {
  const city = findPublishedCityNearby(source);
  const links: VenueInternalLink[] = city
    ? city.domains.slice(0, MAX_CITY_LINKS).map((domain) => ({
        path: cityPagePath(domain, city.slug),
        labelKey: CITY_DOMAIN_LABEL_KEYS[domain],
        cityNameKey: city.nameKey,
      }))
    : [];

  for (const link of CLUSTER_LINKS) {
    if (links.length >= VENUE_INTERNAL_LINK_COUNT) break;
    links.push(link);
  }

  return links;
}

/**
 * The English pages are published under the same slugs (website
 * `generateStaticParams` covers both locales), so the block does not send an
 * English reader to the Italian variant.
 */
export function venueInternalLinkHref(path: string, locale?: string): string {
  const localePrefix = locale && locale !== 'it' ? `/${locale}` : '';
  return `${PUBLIC_SITE_ORIGIN}${localePrefix}${path}`;
}

function cityPagePath(domain: CityDomain, citySlug: string): string {
  return `/cerco-lavoro/${domain}-${citySlug}`;
}

/**
 * Nearest published city whose page covers the venue. The distance is measured
 * against the same centre and radius that page queries with, so the link leads
 * where the venue's own area is. It says nothing about the venue appearing in
 * that list: those pages list job positions, and only 89 of the 2.981 public
 * showcases have one online.
 */
function findPublishedCityNearby(source?: VenueInternalLinksSource | null): PublishedCity | undefined {
  const position = coordinates(source?.lat, source?.lon) ?? coordinates(source?.approximateLat, source?.approximateLon);
  if (!position) return undefined;

  let nearest: { city: PublishedCity; distance: number } | undefined;
  for (const city of PUBLISHED_CITIES) {
    const distance = distanceInMetres(position.lat, position.lon, city.lat, city.lon);
    if (distance > CITY_PAGE_RADIUS_METRES) continue;
    if (!nearest || distance < nearest.distance) nearest = { city, distance };
  }

  return nearest?.city;
}

/**
 * A pair or nothing: the exact and the approximate position come from two
 * different fields, and half of one with half of the other is a point on no map.
 *
 * `minifyRestaurantData` returns `null`, not `undefined`, for a venue without
 * coordinates, and `0` is a legitimate longitude.
 */
function coordinates(lat?: number | null, lon?: number | null): { lat: number; lon: number } | undefined {
  const isCoordinate = (value?: number | null) => typeof value === 'number' && Number.isFinite(value);
  return isCoordinate(lat) && isCoordinate(lon) ? { lat: lat as number, lon: lon as number } : undefined;
}

function distanceInMetres(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const cosine =
    Math.sin(toRadians(lat1)) * Math.sin(toRadians(lat2)) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.cos(toRadians(lon2) - toRadians(lon1));
  // Rounding can push the cosine just past 1 for two identical points, and
  // `Math.acos` answers NaN there.
  return EARTH_RADIUS_METRES * Math.acos(Math.min(1, Math.max(-1, cosine)));
}
